import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import { initStateObject, objectReducer, LOADING, OVERWRITE, FAIL } from 'reducers/default'
import { codifyAPIError } from 'utils/helpers'
import googleConversionGoalsApi from 'domain/googleConversionGoals/api'
import {
  ConversionGoalGroupKey,
  SelectionMode,
  AccountWarningKey,
} from 'domain/googleConversionGoals/schemas'

import SearchDropdownMultiSelect from './Edit/SearchDropdownMultiSelect'

const ABOVE_SELECTOR_WARNING_KEYS = [
  AccountWarningKey.no_conversion_tracking,
  AccountWarningKey.managed_by_another_manager,
]

function useConversionGoals(customerId, { campaignType, biddingStrategy } = {}) {
  const [state, dispatch] = useReducer(objectReducer, initStateObject)
  // Ensure only the latest response updates the state.
  const requestIdRef = useRef(0)
  // Which account the content currently held actually belongs to. Separates a
  // first load (nothing to show yet) from a refetch (list and selection stay
  // on screen), which `loading` alone can't express.
  const settledCustomerIdRef = useRef(null)

  const fetchGoals = useCallback(async () => {
    if (!customerId) return
    const requestId = ++requestIdRef.current
    dispatch({ type: LOADING })
    const response = await googleConversionGoalsApi.fetchConversionGoalsAsync(customerId, {
      campaignType,
      biddingStrategy,
    })
    if (requestId !== requestIdRef.current) return
    settledCustomerIdRef.current = customerId
    if (response.error) {
      dispatch({ type: FAIL, payload: codifyAPIError(response) })
      return
    }
    dispatch({ type: OVERWRITE, payload: response.data })
  }, [customerId, campaignType, biddingStrategy])

  useEffect(() => {
    fetchGoals()
  }, [fetchGoals])

  const isFirstLoad = state.loading && settledCustomerIdRef.current !== customerId

  return [{ ...state, isFirstLoad }, fetchGoals]
}

// Keyed on the discovery item's own `id` (not `resource_name`): the write
// path needs different fields per mode (category entries submit `asset_id`
// = this `id`; a custom_bundle entry submits `resource_name` instead, per
// fd-client-api-google-ads' config_builder.py) — resource_name alone can't
// address a category item, so it's carried as a secondary field instead of
// the tracking key.
function toSelectItem(item) {
  return {
    id: item.id,
    resource_name: item.resource_name,
    name: item.name,
    warnings: item.warnings,
  }
}

export default function GoogleConversionGoalPicker({
  customerId,
  disabled = false,
  campaignType,
  biddingStrategy,
  hasConversionAction = false,
  onSelectionChange,
  onMetaChange,
  initialItemIds,
}) {
  const { t } = useTranslation()
  const [{ content, isFirstLoad, error, success, message }, refetch] = useConversionGoals(
    customerId,
    { campaignType, biddingStrategy },
  )
  const { goals = [], warnings = [], meta = {} } = content

  // Surface the account-level meta signals (notably
  // `account_default_orientation`) up to the caller, which needs them for
  // bidding-strategy suitability, not just this picker's own inline hints.
  // Gated on the fetch having actually settled (success or error) — content
  // (and so `meta`) starts as `{}` before the fetch even begins, and firing
  // on that placeholder lets the caller believe "no biddable default" before
  // it actually knows, computing (and persisting) a bidding-strategy default
  // that gets silently superseded a moment later once the real meta arrives.
  useEffect(() => {
    if (!success && !error) return
    onMetaChange?.(meta)
  }, [meta, success, error, onMetaChange])

  const bundleItems = useMemo(
    () =>
      (goals.find((g) => g.group === ConversionGoalGroupKey.custom_bundles)?.items ?? []).map(
        toSelectItem,
      ),
    [goals],
  )
  // engagement_signals only makes sense for Demand Gen — the backend already
  // omits this group entirely from the response once campaignType is passed,
  // but filter defensively here too since campaignType isn't always known
  const categoryItems = useMemo(
    () =>
      goals
        .filter((g) => g.group !== ConversionGoalGroupKey.custom_bundles)
        .filter(
          (g) =>
            g.group !== ConversionGoalGroupKey.engagement_signals || campaignType === 'DEMAND_GEN',
        )
        .flatMap((g) => g.items)
        .map(toSelectItem),
    [goals, campaignType],
  )

  const [value, setValue] = useState(null)

  useEffect(() => {
    // Reset selection when the underlying account (and therefore the goal
    // list) changes, rather than carrying stale ids across accounts; re-hydrate
    // from the setup's already-persisted selection otherwise (mount, or after
    // a save round-trips fresh integration_details back down).
    setValue(initialItemIds?.length ? initialItemIds.join(',') : null)
  }, [customerId, initialItemIds])

  const handleChange = useCallback(
    (idsString) => {
      setValue(idsString || null)
      if (!idsString) {
        onSelectionChange?.({ mode: null, items: [] })
        return
      }
      const ids = idsString.split(',').filter(Boolean)
      const isBundle = bundleItems.some((b) => ids.includes(b.id))
      const mode = isBundle ? SelectionMode.custom_bundle : SelectionMode.category
      const pool = isBundle ? bundleItems : categoryItems
      const items = ids.map((id) => pool.find((x) => x.id === id)).filter(Boolean)
      onSelectionChange?.({ mode, items })
    },
    [bundleItems, categoryItems, onSelectionChange],
  )

  const renderItemExtra = useCallback(
    (item) =>
      item.warnings?.includes('soft_signal_only') ? (
        <span className="google-conversion-goal-picker_badge">
          {t('Recommendations.campaign.googleConversionGoal.softSignalOnlyLabel')}
        </span>
      ) : null,
    [t],
  )

  const aboveSelectorWarnings = useMemo(
    () => warnings.filter((w) => ABOVE_SELECTOR_WARNING_KEYS.includes(w.key)),
    [warnings],
  )
  const noBiddableGoalsWarning = useMemo(
    () => warnings.find((w) => w.key === AccountWarningKey.no_biddable_goals),
    [warnings],
  )

  if (!customerId) {
    return (
      <p className="google-conversion-goal-picker_placeholder text-muted mb-0">
        {t('Recommendations.campaign.googleConversionGoal.selectAccountFirst')}
      </p>
    )
  }

  const errorText = error ? t(message) : null

  return (
    <div className="google-conversion-goal-picker">
      {aboveSelectorWarnings.map((warning) => (
        <p key={warning.key} className="warning-red google-conversion-goal-picker_tracking-banner">
          {warning.message}
        </p>
      ))}

      <SearchDropdownMultiSelect
        value={value}
        defaultValue={
          isFirstLoad
            ? t('common.loading')
            : t('Recommendations.campaign.googleConversionGoal.inheritDefault')
        }
        onChange={handleChange}
        options={categoryItems}
        savedAudiences={bundleItems}
        disabled={disabled}
        loadingValue={isFirstLoad}
        platform="google"
        loading={{ custom: isFirstLoad, saved: isFirstLoad }}
        error={{ custom: errorText, saved: errorText }}
        onErrorBtnClick={refetch}
        tab0Title={t('Recommendations.campaign.googleConversionGoal.tabs.category')}
        tab1Title={t('Recommendations.campaign.googleConversionGoal.tabs.bundles')}
        tab0Placeholder={t('Recommendations.campaign.googleConversionGoal.searchCategory')}
        tab1Placeholder={t('Recommendations.campaign.googleConversionGoal.searchBundles')}
        renderItemExtra={renderItemExtra}
        showIdSubtext={false}
        dropdownClasses="google-conversion-goal"
      />

      {/* Leaving the selector on "inherit account default" is a working state
          whenever the account's own default biddable goal serves the campaign
          goal — hasConversionAction is what says so, and prompting for an
          explicit pick then is just noise. */}
      {meta.conversion_required && !value && !hasConversionAction && (
        <p className="google-conversion-goal-picker_hint text-muted mb-0">
          {t('Recommendations.campaign.googleConversionGoal.conversionRequiredHint')}
        </p>
      )}

      {noBiddableGoalsWarning && (
        <p
          className="warning-red google-conversion-goal-picker_no-biddable-goals-warning"
          data-testid="conversion-goal-no-biddable-goals-warning"
        >
          {noBiddableGoalsWarning.message}
        </p>
      )}
    </div>
  )
}

GoogleConversionGoalPicker.propTypes = {
  customerId: PropTypes.string,
  disabled: PropTypes.bool,
  campaignType: PropTypes.string,
  biddingStrategy: PropTypes.string,
  hasConversionAction: PropTypes.bool,
  onSelectionChange: PropTypes.func,
  onMetaChange: PropTypes.func,
  initialItemIds: PropTypes.arrayOf(PropTypes.string),
}
